import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  BookText,
  Calculator,
  Check,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'
import { useTableSort } from '../lib/useTableSort'

interface Model111Result {
  year: number
  quarter: 1 | 2 | 3 | 4
  perceptorCount: number
  baseCents: number
  withheldCents: number
  totalToPayCents: number
  effectiveRatePct: number
  missingNifCount: number
  items: Array<{
    expenseId: string
    expenseNumber: string
    date: string
    supplierName: string
    supplierNif: string | null
    baseCents: number
    withheldCents: number
    ratePct: number
  }>
}

interface Model190Result {
  year: number
  perceptorCount: number
  totalBaseCents: number
  totalWithheldCents: number
  missingNifCount: number
  quarterlyTotals: [number, number, number, number]
  perceptors: Array<{
    nif: string | null
    name: string
    clave: string
    subclave: string | null
    baseCents: number
    withheldCents: number
    quarters: [number, number, number, number]
  }>
}

interface Model303Result {
  year: number
  quarter: 1 | 2 | 3 | 4
  outputVatCents: number
  outputBaseCents: number
  inputVatCents: number
  inputBaseCents: number
  resultCents: number
  /** Desgloses que pide la gestoría. Van APARTE: ya suman en los de arriba. */
  rectificationBaseCents: number
  rectificationVatCents: number
  notSubjectBaseCents: number
}

interface Model347Party {
  nif: string
  name: string
  totalCents: number
  byQuarter: [number, number, number, number]
}

interface Model347Result {
  year: number
  threshold: number
  customers: Model347Party[]
  suppliers: Model347Party[]
}

const CURRENT_YEAR = new Date().getFullYear()

/** Pestañas de la pantalla: un modelo cada una. */
type TabId = '303' | 'libros' | '347' | '111' | '190'

export default function AccountingAEAT() {
  const { t } = useTranslation('accounting')

  const QUARTERS: Array<{ value: 1 | 2 | 3 | 4; label: string }> = [
    { value: 1, label: t('quarters.t1') },
    { value: 2, label: t('quarters.t2') },
    { value: 3, label: t('quarters.t3') },
    { value: 4, label: t('quarters.t4') },
  ]

  const [year, setYear] = useState<number>(CURRENT_YEAR)
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>(
    (Math.floor(new Date().getMonth() / 3) + 1) as 1 | 2 | 3 | 4
  )

  /**
   * Un modelo por pestaña. `trimestral` dice si el trimestre pinta algo: el 347
   * y el 190 son anuales, y con ellos delante los botones de trimestre se
   * apagan en vez de dejar creer que filtran algo.
   */
  const TABS: Array<{ id: TabId; label: string; trimestral: boolean }> = [
    { id: '303', label: t('tabs.m303'), trimestral: true },
    { id: 'libros', label: t('tabs.vatBooks'), trimestral: true },
    { id: '347', label: t('tabs.m347'), trimestral: false },
    { id: '111', label: t('tabs.m111'), trimestral: true },
    { id: '190', label: t('tabs.m190'), trimestral: false },
  ]
  const [tabId, setTab] = useState<TabId>('303')
  // `TABS` es literal y siempre trae el 303, pero TypeScript no lo sabe: se le
  // da el valor por defecto a mano en vez de poner un `!`.
  const tabActiva = TABS.find((x) => x.id === tabId) ?? {
    id: '303' as TabId,
    label: t('tabs.m303'),
    trimestral: true,
  }

  const [m303, setM303] = useState<Model303Result | null>(null)
  const [m347, setM347] = useState<Model347Result | null>(null)
  const [loading303, setLoading303] = useState(false)
  const [loading347, setLoading347] = useState(false)
  // F2 — retenciones de IRPF.
  const [m111, setM111] = useState<Model111Result | null>(null)
  const [m190, setM190] = useState<Model190Result | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Orden por columna del detalle del 111 y de los perceptores del 190 (las tablas de los libros de IVA y del 347 llevan el
  // suyo dentro de sus componentes). En la URL (`?sort_m111=…`): al cambiar de ejercicio o trimestre las tablas se repintan
  // y el orden elegido se conserva. ☠️ Los hooks van aquí, por encima de cualquier condicional del pintado.
  const { rows: m111Rows, header: m111Header } = useTableSort(
    m111?.items,
    {
      date: (i) => i.date,
      perceptor: (i) => i.supplierName,
      nif: (i) => i.supplierNif,
      base: (i) => i.baseCents,
      rate: (i) => i.ratePct,
      withheld: (i) => i.withheldCents,
    },
    { id: 'm111' }
  )
  const { rows: m190Rows, header: m190Header } = useTableSort(
    m190?.perceptors,
    {
      nif: (p) => p.nif,
      perceptor: (p) => p.name,
      clave: (p) => p.clave,
      subclave: (p) => p.subclave,
      base: (p) => p.baseCents,
      // Los trimestres a cero se pintan en blanco: se tratan como vacío (van al final).
      t1: (p) => p.quarters[0] || null,
      t2: (p) => p.quarters[1] || null,
      t3: (p) => p.quarters[2] || null,
      t4: (p) => p.quarters[3] || null,
      withheld: (p) => p.withheldCents,
    },
    { id: 'm190' }
  )

  // Con botones no caben ocho años, ni hacen falta: el 303 se presenta del
  // trimestre en curso y, como mucho, se mira el ejercicio anterior.
  const yearOptions = useMemo(
    () => [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2],
    []
  )

  const load303 = useCallback(async () => {
    setLoading303(true)
    setError(null)
    try {
      const data = await api.get<Model303Result>(
        `/accounting/aeat/303?year=${year}&quarter=${quarter}`
      )
      setM303(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('errors.calc303')
      setError(text)
      setM303(null)
    } finally {
      setLoading303(false)
    }
  }, [year, quarter, t])

  const load347 = useCallback(async () => {
    setLoading347(true)
    setError(null)
    try {
      const data = await api.get<Model347Result>(`/accounting/aeat/347?year=${year}`)
      setM347(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('errors.calc347')
      setError(text)
      setM347(null)
    } finally {
      setLoading347(false)
    }
  }, [year, t])

  // F2 — 111 (trimestral) y 190 (anual). Fallan en silencio para no tapar el
  // resto de la página si algo va mal en un modelo concreto.
  const load111 = useCallback(async () => {
    try {
      setM111(await api.get<Model111Result>(`/accounting/aeat/111?year=${year}&quarter=${quarter}`))
    } catch {
      setM111(null)
    }
  }, [year, quarter])

  const load190 = useCallback(async () => {
    try {
      setM190(await api.get<Model190Result>(`/accounting/aeat/190?year=${year}`))
    } catch {
      setM190(null)
    }
  }, [year])

  useEffect(() => {
    void load111()
  }, [load111])
  useEffect(() => {
    void load190()
  }, [load190])

  useEffect(() => {
    void load303()
  }, [load303])
  useEffect(() => {
    void load347()
  }, [load347])

  const download303 = async () => {
    try {
      await downloadFile(
        `/accounting/aeat/303/csv?year=${year}&quarter=${quarter}`,
        `modelo-303-${year}-T${quarter}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const download347 = async () => {
    try {
      await downloadFile(`/accounting/aeat/347/csv?year=${year}`, `modelo-347-${year}.csv`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const download111 = async () => {
    try {
      await downloadFile(
        `/accounting/aeat/111/csv?year=${year}&quarter=${quarter}`,
        `modelo-111-${year}T${quarter}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const download190 = async () => {
    try {
      await downloadFile(`/accounting/aeat/190/csv?year=${year}`, `modelo-190-${year}.csv`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  // Los borradores «tipo AEAT» en PDF: el papel que se revisa y se le manda a
  // la gestoría. Llevan dentro su marca de agua: no se pueden presentar.
  const downloadPdf = async (path: string, name: string) => {
    try {
      await downloadFile(path, name)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const resultLabel = (cents: number): { text: string; tone: string } => {
    if (cents > 0) return { text: t('result.toPay'), tone: 'text-rose-700 dark:text-rose-300' }
    if (cents < 0)
      return { text: t('result.toOffset'), tone: 'text-emerald-700 dark:text-emerald-300' }
    return { text: t('result.zero'), tone: 't-muted' }
  }

  return (
    <div className="space-y-5 animate-slideUp">
      <header className="space-y-3">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Calculator size={20} className="text-jr2-700 dark:text-jr2-300" />
            {t('aeatTitle')}
          </h1>
          <p className="text-xs t-muted">{t('aeatSubtitle')}</p>
        </div>

        {/* Ejercicio y trimestre, con botones. Este filtro MANDA sobre todo lo
            que hay debajo: antes cada bloque traía el suyo y se podía estar
            mirando el 303 del T3 con los libros del año entero al lado. */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider t-muted mr-1">
              {t('filters.year')}
            </span>
            {yearOptions.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setYear(y)}
                aria-pressed={y === year}
                className={[
                  'px-2.5 py-1 rounded-md text-sm tabular-nums border transition',
                  y === year
                    ? 'bg-jr2-600 text-white border-jr2-600'
                    : 'border-base t-secondary hover:bg-hover',
                ].join(' ')}
              >
                {y}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wider t-muted mr-1">
              {t('filters.quarter')}
            </span>
            {QUARTERS.map((q) => (
              <button
                key={q.value}
                type="button"
                onClick={() => setQuarter(q.value)}
                disabled={!tabActiva.trimestral}
                aria-pressed={q.value === quarter}
                title={tabActiva.trimestral ? undefined : t('filters.annualModel')}
                className={[
                  'px-2.5 py-1 rounded-md text-sm border transition disabled:opacity-40 disabled:cursor-not-allowed',
                  q.value === quarter && tabActiva.trimestral
                    ? 'bg-jr2-600 text-white border-jr2-600'
                    : 'border-base t-secondary hover:bg-hover disabled:hover:bg-transparent',
                ].join(' ')}
              >
                {q.label}
              </button>
            ))}
          </div>

          <span className="text-[11px] t-faint">
            {tabActiva.trimestral
              ? t('filters.appliesQuarter', { year, quarter })
              : t('filters.appliesYear', { year })}
          </span>
        </div>

        {/* Un modelo por pestaña, en vez de los cinco en vertical. */}
        <nav className="flex flex-wrap gap-1 border-b border-base" aria-label={t('aeatTitle')}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              aria-current={tab.id === tabId ? 'page' : undefined}
              className={[
                'px-3 py-2 text-sm -mb-px border-b-2 transition',
                tab.id === tabId
                  ? 'border-jr2-600 t-primary font-semibold'
                  : 'border-transparent t-muted hover:t-secondary',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      {error && (
        <div className="surface p-3 flex items-start gap-2 border-rose-500/40">
          <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
        </div>
      )}

      {tabId === '303' && (
        <>
          {/* ===================== Modelo 303 ===================== */}
          <section className="surface p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold t-primary flex items-center gap-2">
                  <FileSpreadsheet size={18} className="text-jr2-700 dark:text-jr2-300" />
                  {t('m303.title', { year, quarter })}
                </h2>
                <p className="text-xs t-muted">{t('m303.subtitle')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void downloadPdf(
                      `/accounting/aeat/303/pdf?year=${year}&quarter=${quarter}`,
                      `borrador-303-${year}-${quarter}T.pdf`
                    )
                  }
                  disabled={loading303}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-base hover:bg-hover t-secondary flex items-center gap-1.5 disabled:opacity-50"
                >
                  <FileText size={14} />
                  {t('m303.downloadPdf')}
                </button>
                <button
                  type="button"
                  onClick={download303}
                  disabled={loading303}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Download size={14} />
                  {t('m303.downloadCsv')}
                </button>
              </div>
            </div>

            {loading303 ? (
              <div className="t-muted flex items-center gap-2 text-sm">
                <Loader2 size={16} className="animate-spin" /> {t('calculating')}
              </div>
            ) : m303 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Card
                  label={t('m303.outputBase')}
                  value={formatCents(m303.outputBaseCents)}
                  hint={t('m303.outputBaseHint')}
                />
                <Card
                  label={t('m303.outputVat')}
                  value={formatCents(m303.outputVatCents)}
                  hint={t('m303.outputVatHint')}
                />
                <div />
                <Card
                  label={t('m303.inputBase')}
                  value={formatCents(m303.inputBaseCents)}
                  hint={t('m303.inputBaseHint')}
                />
                <Card
                  label={t('m303.inputVat')}
                  value={formatCents(m303.inputVatCents)}
                  hint={t('m303.inputVatHint')}
                />
                <Card
                  label={t('m303.result')}
                  value={formatCents(m303.resultCents)}
                  hint={resultLabel(m303.resultCents).text}
                  hintClass={resultLabel(m303.resultCents).tone}
                  accent
                />
              </div>
            ) : (
              <p className="text-sm t-muted">{t('noData')}</p>
            )}

            {/* Desgloses con casilla propia en el modelo. Solo se enseñan
                cuando hay algo que enseñar: una empresa que no rectifica ni
                factura fuera del territorio no tiene por qué verlos. */}
            {m303 && (m303.rectificationBaseCents !== 0 || m303.notSubjectBaseCents !== 0) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                {m303.rectificationBaseCents !== 0 && (
                  <Card
                    label={t('m303.rectifications')}
                    value={formatCents(m303.rectificationBaseCents)}
                    hint={t('m303.rectificationsHint', {
                      iva: formatCents(m303.rectificationVatCents),
                    })}
                  />
                )}
                {m303.notSubjectBaseCents !== 0 && (
                  <Card
                    label={t('m303.notSubject')}
                    value={formatCents(m303.notSubjectBaseCents)}
                    hint={t('m303.notSubjectHint')}
                  />
                )}
              </div>
            ) : null}
          </section>
        </>
      )}

      {tabId === 'libros' && <VatBooksSection year={year} quarter={quarter} />}

      {tabId === '347' && (
        <>
          {/* ===================== Modelo 347 ===================== */}
          <section className="surface p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold t-primary flex items-center gap-2">
                  <FileSpreadsheet size={18} className="text-jr2-700 dark:text-jr2-300" />
                  {t('m347.title', { year })}
                </h2>
                <p className="text-xs t-muted">{t('m347.subtitle')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void downloadPdf(
                      `/accounting/aeat/347/pdf?year=${year}`,
                      `borrador-347-${year}.pdf`
                    )
                  }
                  disabled={loading347}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-base hover:bg-hover t-secondary flex items-center gap-1.5 disabled:opacity-50"
                >
                  <FileText size={14} />
                  {t('m347.downloadPdf')}
                </button>
                <button
                  type="button"
                  onClick={download347}
                  disabled={loading347}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Download size={14} />
                  {t('m347.downloadCsv')}
                </button>
              </div>
            </div>

            {loading347 ? (
              <div className="t-muted flex items-center gap-2 text-sm">
                <Loader2 size={16} className="animate-spin" /> {t('calculating')}
              </div>
            ) : m347 ? (
              <div className="space-y-5">
                <PartyTable
                  title={t('m347.customers')}
                  parties={m347.customers}
                  sortId="m347Customers"
                />
                <PartyTable
                  title={t('m347.suppliers')}
                  parties={m347.suppliers}
                  sortId="m347Suppliers"
                />
              </div>
            ) : (
              <p className="text-sm t-muted">{t('noData')}</p>
            )}
          </section>
        </>
      )}

      {tabId === '111' && (
        <>
          {/* ---- F2: Modelo 111 (retenciones IRPF del trimestre) ---- */}
          <section
            className="rounded-xl border p-4 space-y-3"
            style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
          >
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold t-primary">
                  {t('m111.title', { year, quarter })}
                </h2>
                <p className="text-xs t-muted">{t('m111.subtitle')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void downloadPdf(
                      `/accounting/aeat/111/pdf?year=${year}&quarter=${quarter}`,
                      `borrador-111-${year}-${quarter}T.pdf`
                    )
                  }
                  className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5"
                >
                  <FileText size={12} />
                  {t('m111.downloadPdf')}
                </button>
                <button
                  type="button"
                  onClick={() => void download111()}
                  className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary"
                >
                  {t('m111.download')}
                </button>
              </div>
            </header>

            {m111 === null ? (
              <p className="text-xs t-muted">{t('m111.empty')}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider t-faint">
                      {t('m111.perceptors')}
                    </div>
                    <div className="font-mono t-primary">{m111.perceptorCount}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider t-faint">
                      {t('m111.base')}
                    </div>
                    <div className="font-mono t-primary">{formatCents(m111.baseCents)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider t-faint">
                      {t('m111.rate')}
                    </div>
                    <div className="font-mono t-primary">{m111.effectiveRatePct.toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-300">
                      {t('m111.toPay')}
                    </div>
                    <div className="font-mono font-semibold t-primary">
                      {formatCents(m111.totalToPayCents)}
                    </div>
                  </div>
                </div>

                {m111.missingNifCount > 0 && (
                  <div className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-300">
                    {t('m111.missingNif', { count: m111.missingNifCount })}
                  </div>
                )}

                {m111.items.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider t-faint">
                          {m111Header('date', t('m111.col.date'), { className: 'py-1' })}
                          {m111Header('perceptor', t('m111.col.perceptor'), { className: 'py-1' })}
                          {m111Header('nif', t('m111.col.nif'), { className: 'py-1' })}
                          {m111Header('base', t('m111.col.base'), {
                            align: 'right',
                            className: 'py-1',
                          })}
                          {m111Header('rate', t('m111.col.rate'), {
                            align: 'right',
                            className: 'py-1',
                          })}
                          {m111Header('withheld', t('m111.col.withheld'), {
                            align: 'right',
                            className: 'py-1',
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {m111Rows.map((i) => (
                          <tr key={i.expenseId} className="border-t border-base/40">
                            <td className="py-1 t-secondary text-xs">{i.date.slice(0, 10)}</td>
                            <td className="py-1 t-primary">{i.supplierName}</td>
                            <td className="py-1 font-mono text-xs t-muted">
                              {i.supplierNif ?? (
                                <span className="text-amber-700 dark:text-amber-300">
                                  {t('m111.noNif')}
                                </span>
                              )}
                            </td>
                            <td className="py-1 text-right font-mono tabular-nums t-secondary">
                              {formatCents(i.baseCents)}
                            </td>
                            <td className="py-1 text-right font-mono tabular-nums t-muted">
                              {i.ratePct.toFixed(1)}%
                            </td>
                            <td className="py-1 text-right font-mono tabular-nums t-primary">
                              {formatCents(i.withheldCents)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {tabId === '190' && (
        <>
          {/* ---- F2: Modelo 190 (resumen anual por perceptor) ---- */}
          <section
            className="rounded-xl border p-4 space-y-3"
            style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
          >
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold t-primary">{t('m190.title', { year })}</h2>
                <p className="text-xs t-muted">{t('m190.subtitle')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void downloadPdf(
                      `/accounting/aeat/190/pdf?year=${year}`,
                      `borrador-190-${year}.pdf`
                    )
                  }
                  className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5"
                >
                  <FileText size={12} />
                  {t('m190.downloadPdf')}
                </button>
                <button
                  type="button"
                  onClick={() => void download190()}
                  className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary"
                >
                  {t('m190.download')}
                </button>
              </div>
            </header>

            {m190 === null || m190.perceptors.length === 0 ? (
              <p className="text-xs t-muted">{t('m190.empty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider t-faint">
                      {m190Header('nif', t('m190.col.nif'), { className: 'py-1' })}
                      {m190Header('perceptor', t('m190.col.perceptor'), { className: 'py-1' })}
                      {m190Header('clave', t('m190.col.clave'), {
                        align: 'center',
                        className: 'py-1',
                      })}
                      {m190Header('subclave', t('m190.col.subclave'), {
                        align: 'center',
                        className: 'py-1',
                      })}
                      {m190Header('base', t('m190.col.base'), {
                        align: 'right',
                        className: 'py-1',
                      })}
                      {m190Header('t1', 'T1', { align: 'right', className: 'py-1' })}
                      {m190Header('t2', 'T2', { align: 'right', className: 'py-1' })}
                      {m190Header('t3', 'T3', { align: 'right', className: 'py-1' })}
                      {m190Header('t4', 'T4', { align: 'right', className: 'py-1' })}
                      {m190Header('withheld', t('m190.col.withheld'), {
                        align: 'right',
                        className: 'py-1',
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {m190Rows.map((p) => (
                      <tr key={`${p.nif ?? p.name}`} className="border-t border-base/40">
                        <td className="py-1 font-mono text-xs t-muted">
                          {p.nif ?? (
                            <span className="text-amber-700 dark:text-amber-300">
                              {t('m111.noNif')}
                            </span>
                          )}
                        </td>
                        <td className="py-1 t-primary">{p.name}</td>
                        <td className="py-1 text-center font-mono text-xs t-muted">{p.clave}</td>
                        <td className="py-1 text-center font-mono text-xs t-muted">
                          {p.subclave ?? (
                            <span className="text-amber-700 dark:text-amber-300">—</span>
                          )}
                        </td>
                        <td className="py-1 text-right font-mono tabular-nums t-secondary">
                          {formatCents(p.baseCents)}
                        </td>
                        {p.quarters.map((q, i) => (
                          <td key={i} className="py-1 text-right font-mono tabular-nums t-muted">
                            {q ? formatCents(q) : ''}
                          </td>
                        ))}
                        <td className="py-1 text-right font-mono tabular-nums font-semibold t-primary">
                          {formatCents(p.withheldCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-base font-semibold">
                      <td className="pt-2" colSpan={4}>
                        {t('m190.total')}
                      </td>
                      <td className="pt-2 text-right font-mono">
                        {formatCents(m190.totalBaseCents)}
                      </td>
                      {m190.quarterlyTotals.map((q, i) => (
                        <td key={i} className="pt-2 text-right font-mono">
                          {q ? formatCents(q) : ''}
                        </td>
                      ))}
                      <td className="pt-2 text-right font-mono">
                        {formatCents(m190.totalWithheldCents)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

// ----------------- Componentes auxiliares -----------------

interface CardProps {
  label: string
  value: string
  hint?: string
  hintClass?: string
  accent?: boolean
}

function Card({ label, value, hint, hintClass, accent }: CardProps) {
  return (
    <div
      className={[
        'rounded-lg border border-base p-3 flex flex-col gap-1',
        accent ? 'bg-jr2-500/10' : 'bg-transparent',
      ].join(' ')}
    >
      <span className="text-[11px] uppercase tracking-wider t-muted">{label}</span>
      <span className="text-lg font-semibold t-primary tabular-nums">{value}</span>
      {hint && <span className={`text-[11px] ${hintClass ?? 't-faint'}`}>{hint}</span>}
    </div>
  )
}

// ============================================================================
// F2-B — Libros registro de IVA (expedidas y recibidas).
//
// Lo que aporta sobre el 303 no es el total, es la fila: el 303 da cuatro
// cifras y el libro da las facturas que las sostienen. Y como el 303 se calcula
// del DIARIO y el libro de las FACTURAS, contrastarlos detecta lo que ningún
// informe aislado ve — una factura sin contabilizar, o un asiento manual con
// IVA que nadie registró como factura.
// ============================================================================

interface VatRateBreakdown {
  ratePct: number
  baseCents: number
  vatCents: number
}

interface VatBookRow {
  id: string
  issueDate: string
  recordDate?: string
  number: string
  partyName: string
  partyNif: string | null
  breakdown: VatRateBreakdown[]
  baseCents: number
  vatCents: number
  irpfCents: number
  totalCents: number
  kind: string
  deductibleVatCents?: number
  /** La operación va con inversión del sujeto pasivo: la cuota es la autorrepercutida. */
  reverseCharge?: boolean
}

interface VatBook {
  rows: VatBookRow[]
  totals: {
    baseCents: number
    vatCents: number
    irpfCents: number
    totalCents: number
    byRate: VatRateBreakdown[]
    reverseChargeVatCents: number
  }
}

interface VatBooksResult {
  year: number
  quarter: 1 | 2 | 3 | 4 | null
  issued: VatBook
  received: VatBook
  reconciliation: {
    bookOutputVatCents: number
    /** Cuota autorrepercutida: el 303 la devenga pero no es factura expedida. */
    reverseChargeVatCents: number
    model303OutputVatCents: number
    outputDiffCents: number
    bookInputVatCents: number
    model303InputVatCents: number
    inputDiffCents: number
    matches: boolean
  }
}

function VatBooksSection({ year, quarter }: { year: number; quarter: 1 | 2 | 3 | 4 }) {
  const { t } = useTranslation('accounting')
  const [data, setData] = useState<VatBooksResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // El periodo es el de la cabecera, no uno propio. Antes este bloque traía su
  // propio selector («trimestre T3» o «año completo») y se podía acabar
  // leyendo el 303 del T3 con el libro del año entero al lado, que es la mejor
  // forma de cuadrar mal un modelo.
  const params = `year=${year}&quarter=${quarter}`

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await api.get<VatBooksResult>(`/accounting/vat-books?${params}`))
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('vatBooks.errorLoad')
      setError(text)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [params, t])

  useEffect(() => {
    void load()
  }, [load])

  const download = async (book: 'issued' | 'received') => {
    const period = `${year}T${quarter}`
    const name = book === 'received' ? 'recibidas' : 'expedidas'
    try {
      await downloadFile(
        `/accounting/vat-books/csv?${params}&book=${book}`,
        `libro-iva-${name}-${period}.csv`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  // El libro registro en PDF. No lleva marca de agua de borrador: impreso ES el
  // libro (art. 62 RIVA), a diferencia de los modelos.
  const downloadBookPdf = async (book: 'issued' | 'received') => {
    const period = `${year}T${quarter}`
    const name = book === 'received' ? 'recibidas' : 'expedidas'
    try {
      await downloadFile(
        `/accounting/vat-books/pdf?${params}&book=${book}`,
        `libro-iva-${name}-${period}.pdf`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.download'))
    }
  }

  const rec = data?.reconciliation

  return (
    <section className="surface p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold t-primary flex items-center gap-2">
            <BookText size={18} className="text-jr2-700 dark:text-jr2-300" />
            {t('vatBooks.title')}
          </h2>
          <p className="text-xs t-muted">{t('vatBooks.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => void download('issued')}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} /> {t('vatBooks.downloadIssued')}
          </button>
          <button
            type="button"
            onClick={() => void downloadBookPdf('issued')}
            disabled={loading}
            className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5 disabled:opacity-50"
          >
            <FileText size={14} /> {t('vatBooks.pdfIssued')}
          </button>
          <button
            type="button"
            onClick={() => void download('received')}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 flex items-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} /> {t('vatBooks.downloadReceived')}
          </button>
          <button
            type="button"
            onClick={() => void downloadBookPdf('received')}
            disabled={loading}
            className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5 disabled:opacity-50"
          >
            <FileText size={14} /> {t('vatBooks.pdfReceived')}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-muted flex items-center gap-2 text-sm">
          <Loader2 size={16} className="animate-spin" /> {t('calculating')}
        </div>
      ) : data && rec ? (
        <div className="space-y-5">
          {/* Contraste con el 303 — lo primero que hay que mirar. */}
          <div
            className={[
              'rounded-lg border p-3 text-sm flex items-start gap-2',
              rec.matches
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
                : 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300',
            ].join(' ')}
          >
            {rec.matches ? (
              <Check size={15} className="mt-0.5 shrink-0" />
            ) : (
              <AlertCircle size={15} className="mt-0.5 shrink-0" />
            )}
            <div>
              <p className="font-medium">
                {rec.matches ? t('vatBooks.reconOk') : t('vatBooks.reconMismatch')}
              </p>
              {!rec.matches && (
                <ul className="text-xs mt-1 space-y-0.5">
                  {rec.outputDiffCents !== 0 && (
                    <li>
                      {t('vatBooks.reconOutput', {
                        book: formatCents(rec.bookOutputVatCents + rec.reverseChargeVatCents),
                        model: formatCents(rec.model303OutputVatCents),
                        diff: formatCents(rec.outputDiffCents),
                      })}
                    </li>
                  )}
                  {rec.inputDiffCents !== 0 && (
                    <li>
                      {t('vatBooks.reconInput', {
                        book: formatCents(rec.bookInputVatCents),
                        model: formatCents(rec.model303InputVatCents),
                        diff: formatCents(rec.inputDiffCents),
                      })}
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <VatBookTable title={t('vatBooks.issued')} book={data.issued} kind="issued" />
          <VatBookTable title={t('vatBooks.received')} book={data.received} kind="received" />
        </div>
      ) : (
        <p className="text-sm t-muted">{t('noData')}</p>
      )}
    </section>
  )
}

function VatBookTable({
  title,
  book,
  kind,
}: {
  title: string
  book: VatBook
  kind: 'issued' | 'received'
}) {
  const { t } = useTranslation('accounting')
  const isReceived = kind === 'received'

  // Orden por columna de la vista en pantalla (los CSV y el PDF del libro salen del servidor en su orden oficial). Cada libro
  // lleva su `id`: se enseñan los dos a la vez. Los totales salen de `book.totals`, no de las filas ordenadas.
  const { rows, header } = useTableSort(
    book.rows,
    {
      date: (r) => r.issueDate,
      recordDate: (r) => r.recordDate,
      number: (r) => r.number,
      nif: (r) => r.partyNif,
      party: (r) => r.partyName,
      rates: (r) => r.breakdown.map((b) => `${b.ratePct}%`).join(' + '),
      base: (r) => r.baseCents,
      vat: (r) => r.vatCents,
      // Sin retención se pinta en blanco: se trata como vacío (va al final).
      irpf: (r) => r.irpfCents || null,
      total: (r) => r.totalCents,
    },
    { id: `vat_${kind}` }
  )

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
        <h3 className="text-sm font-semibold t-secondary">
          {title} <span className="t-faint text-xs">({book.rows.length})</span>
        </h3>
        {book.totals.byRate.length > 0 && (
          <span className="text-[11px] t-faint">
            {book.totals.byRate
              .map((r) => `${r.ratePct}% · ${formatCents(r.baseCents)}`)
              .join('   ·   ')}
          </span>
        )}
      </div>

      {book.rows.length === 0 ? (
        <p className="text-xs t-muted">{t('vatBooks.noRows')}</p>
      ) : (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0" style={{ background: 'var(--bg-card)' }}>
              <tr className="text-[11px] uppercase tracking-wider t-muted border-b border-base">
                {header('date', t('vatBooks.cols.date'), { className: 'py-2 pr-3 font-medium' })}
                {isReceived &&
                  header('recordDate', t('vatBooks.cols.recordDate'), {
                    className: 'py-2 pr-3 font-medium',
                  })}
                {header('number', t('vatBooks.cols.number'), {
                  className: 'py-2 pr-3 font-medium',
                })}
                {header('nif', t('vatBooks.cols.nif'), { className: 'py-2 pr-3 font-medium' })}
                {header(
                  'party',
                  isReceived ? t('vatBooks.cols.supplier') : t('vatBooks.cols.customer'),
                  { className: 'py-2 pr-3 font-medium' }
                )}
                {header('rates', t('vatBooks.cols.rates'), { className: 'py-2 pr-3 font-medium' })}
                {header('base', t('vatBooks.cols.base'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('vat', t('vatBooks.cols.vat'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('irpf', t('vatBooks.cols.irpf'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('total', t('vatBooks.cols.total'), {
                  align: 'right',
                  className: 'py-2 pl-2 font-medium',
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-base/50 last:border-0">
                  <td className="py-1.5 pr-3 t-secondary text-xs tabular-nums whitespace-nowrap">
                    {r.issueDate.slice(0, 10)}
                  </td>
                  {isReceived && (
                    <td className="py-1.5 pr-3 t-faint text-xs tabular-nums whitespace-nowrap">
                      {(r.recordDate ?? '').slice(0, 10)}
                    </td>
                  )}
                  <td className="py-1.5 pr-3 t-primary text-xs font-mono whitespace-nowrap">
                    {r.number || '—'}
                    {r.kind === 'RECTIFICATIVA' && (
                      <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-300">R</span>
                    )}
                    {r.reverseCharge && (
                      <span
                        className="ml-1 text-[10px] text-sky-700 dark:text-sky-300"
                        title={t('vatBooks.reverseChargeHint')}
                      >
                        ISP
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 t-secondary text-xs tabular-nums">
                    {r.partyNif ?? (
                      <span className="text-amber-700 dark:text-amber-300">
                        {t('vatBooks.noNif')}
                      </span>
                    )}
                  </td>
                  <td
                    className="py-1.5 pr-3 t-primary text-xs max-w-[16rem] truncate"
                    title={r.partyName}
                  >
                    {r.partyName}
                  </td>
                  <td className="py-1.5 pr-3 t-faint text-[11px] whitespace-nowrap">
                    {r.breakdown.map((b) => `${b.ratePct}%`).join(' + ') || '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums t-secondary text-xs">
                    {formatCents(r.baseCents)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums t-secondary text-xs">
                    {formatCents(r.vatCents)}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums t-faint text-xs">
                    {r.irpfCents ? formatCents(r.irpfCents) : ''}
                  </td>
                  <td className="py-1.5 pl-2 text-right tabular-nums font-semibold t-primary text-xs">
                    {formatCents(r.totalCents)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold t-primary border-t border-base">
                <td className="py-2 pr-3 text-xs" colSpan={isReceived ? 6 : 5}>
                  {t('vatBooks.cols.totals')}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.baseCents)}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.vatCents)}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.irpfCents)}
                </td>
                <td className="py-2 pl-2 text-right tabular-nums text-xs">
                  {formatCents(book.totals.totalCents)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

function PartyTable({
  title,
  parties,
  sortId,
}: {
  title: string
  parties: Model347Party[]
  /** Identifica el orden de esta tabla en la URL: se enseñan dos a la vez (clientes y proveedores). */
  sortId: string
}) {
  const { t } = useTranslation('accounting')
  const { rows, header } = useTableSort(
    parties,
    {
      nif: (p) => p.nif,
      name: (p) => p.name,
      t1: (p) => p.byQuarter[0],
      t2: (p) => p.byQuarter[1],
      t3: (p) => p.byQuarter[2],
      t4: (p) => p.byQuarter[3],
      total: (p) => p.totalCents,
    },
    { id: sortId }
  )
  return (
    <div>
      <h3 className="text-sm font-semibold t-secondary mb-2">
        {title} <span className="t-faint text-xs">({parties.length})</span>
      </h3>
      {parties.length === 0 ? (
        <p className="text-xs t-muted">{t('m347.noOps')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider t-muted border-b border-base">
                {header('nif', t('m347.cols.nif'), { className: 'py-2 pr-3 font-medium' })}
                {header('name', t('m347.cols.name'), { className: 'py-2 pr-3 font-medium' })}
                {header('t1', t('m347.cols.t1'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('t2', t('m347.cols.t2'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('t3', t('m347.cols.t3'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('t4', t('m347.cols.t4'), {
                  align: 'right',
                  className: 'py-2 px-2 font-medium',
                })}
                {header('total', t('m347.cols.total'), {
                  align: 'right',
                  className: 'py-2 pl-2 font-medium',
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.nif + p.name} className="border-b border-base/50 last:border-0">
                  <td className="py-2 pr-3 t-secondary tabular-nums">{p.nif}</td>
                  <td className="py-2 pr-3 t-primary">{p.name}</td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[0])}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[1])}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[2])}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums t-secondary">
                    {formatCents(p.byQuarter[3])}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums font-semibold t-primary">
                    {formatCents(p.totalCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
